import type { ReactNode } from 'react';
import { Button, Dialog, DialogTrigger } from 'react-aria-components';
import { Popover } from '../tailgrids/core/popover.js';
import { cn } from '../../utils/cn.js';

/**
 * A value that opens its breakdown: the trigger is the value itself (a score badge, a figure), the popover explains it
 * (NextAdmin `ui-elements/popovers`; built like `HelpIcon`). Keyboard reachable (Tab, Enter/Space), closes on Escape
 * or an outside click, themed through the palette in light and dark. Clicking it never reaches a clickable row
 * behind it.
 */
export function InfoPopover({
  trigger,
  label,
  children,
  className,
}: {
  /** What is shown in place (the value). */
  trigger: ReactNode;
  /** Names the trigger and the popover for screen readers ("Score breakdown"). */
  label: string;
  /** The breakdown. */
  children: ReactNode;
  className?: string;
}) {
  return (
    <DialogTrigger>
      <Button
        slot={null}
        aria-label={label}
        // A row with `onRowClick` would open its item on the same click: the breakdown is a step aside, not a navigation.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className={cn(
          'inline-flex items-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent/40',
          className,
        )}
      >
        {trigger}
      </Button>
      {/* `bg-card-background`: the vendored popover paints `background-100` (#111827 in dark), not a NextAdmin surface. */}
      <Popover
        placement="bottom start"
        className="max-h-[60vh] max-w-sm overflow-auto bg-card-background p-4 shadow-lg"
      >
        <Dialog aria-label={label} className="text-xs leading-relaxed text-body outline-none">
          {children}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
